// Pure control-bar policy: given the app phase + flags (unsaved edits, whether there's content to
// clear), decide which buttons are disabled. Kept DOM-free so the matrix — including the anti-strand
// invariant (a non-busy state always leaves Import or Clear reachable) — is unit-testable.
// setPhase() in main.ts is the only consumer; it just maps this result onto the DOM.

export type Phase = 'idle' | 'importing' | 'imported' | 'running' | 'done';

export interface ControlFlags {
  /** The scorecard has unsaved edits vs. the applied baseline (gates Save + Reset). */
  dirty: boolean;
  /** There is something to clear — a transcript has been imported this session (gates Clear).
   *  False on a pristine load or right after Clear, so Clear isn't offered with nothing to reset. */
  hasContent: boolean;
}

/** `disabled` state for every control-bar button in a given phase. `busy` = a job is in flight.
 *  `runAwaiting` is a positive hint (not a disabled flag): the app is idle-waiting for the user to
 *  hit Run after a successful import — drives the soft Run-button flicker. */
export interface ControlState {
  busy: boolean;
  runAwaiting: boolean;
  import: boolean;
  run: boolean;
  stop: boolean;
  pdf: boolean;
  clear: boolean;
  save: boolean;
  reset: boolean;
}

export function controlStates(phase: Phase, flags: ControlFlags): ControlState {
  const busy = phase === 'running' || phase === 'importing';
  return {
    busy,
    // Prompt the user toward Run only in the freshly-imported, not-yet-scored state.
    runAwaiting: phase === 'imported',
    // Import: blocked only while a job runs.
    import: busy,
    // Run: available once a transcript is loaded (score it) or already scored (re-score) — off while
    // busy. The message center tells a fresh import to click Run; Run itself behaves normally.
    run: phase !== 'imported' && phase !== 'done',
    // Stop: active only while there is an in-flight import/scoring job to cancel.
    stop: !busy,
    // Download PDF: only a completed scoring has a report to export.
    pdf: phase !== 'done',
    // Clear: the recovery hatch. Enabled when there's content to clear and no job is running —
    // including 'idle' after a sanity Fail / import error (there's leftover state to reset). On a
    // pristine load / just after Clear there's nothing to clear, so it's disabled; Import is the
    // action then, so the user is never stranded.
    clear: busy || !flags.hasContent,
    // Save / Reset: only when the scorecard has unsaved edits, and never while busy. (main.ts also
    // gates Save on the call type being identified — you can't customize an unidentified call.)
    save: busy || !flags.dirty,
    reset: busy || !flags.dirty,
  };
}
